import React, { useState, useEffect } from 'react';
import { get } from 'aws-amplify/api';
import { Photo } from '../types/Photo';
import './Search.css';

export const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useState({
    lens: '',
    camera: '',
    dateFrom: '',
    dateTo: ''
  });
  const [results, setResults] = useState<Photo[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [cameraOptions, setCameraOptions] = useState<string[]>([]);
  const [lensOptions, setLensOptions] = useState<string[]>([]);
  const [showCameraSuggestions, setShowCameraSuggestions] = useState(false);
  const [showLensSuggestions, setShowLensSuggestions] = useState(false);

  useEffect(() => {
    loadAutocompleteData();
  }, []);

  const loadAutocompleteData = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const restOperation = get({
        apiName: 'PhotosynthesizeAPI',
        path: '/stats',
        options: {
          queryParams: { period: '365d' },
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        }
      });
      const { body } = await restOperation.response;
      const response = await body.json() as any;

      // Extract unique camera and lens models from stats
      if (response?.camera_usage) {
        setCameraOptions(Object.keys(response.camera_usage));
      }
      if (response?.lens_usage) {
        setLensOptions(Object.keys(response.lens_usage));
      }
    } catch (error) {
      console.error('Failed to load autocomplete data:', error);
      // Fallback to common camera and lens options
      setCameraOptions([
        'Canon EOS R5',
        'Canon EOS R6',
        'Canon EOS R6 Mark II',
        'Canon EOS R3',
        'Sony A7 IV',
        'Sony A7R V',
        'Nikon Z8',
        'Nikon Z9'
      ]);
      setLensOptions([
        'RF 100-500mm F4.5-7.1 L IS USM',
        'RF 70-200mm F2.8 L IS USM',
        'RF 24-70mm F2.8 L IS USM',
        'RF 100mm F2.8 L Macro IS USM',
        'Sony FE 200-600mm F5.6-6.3 G OSS',
        'Nikkor Z 100-400mm f/4.5-5.6 VR S'
      ]);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSearching(true);
    setSearched(true);

    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const params: any = {};
      if (searchParams.lens) params.lens = searchParams.lens;
      if (searchParams.camera) params.camera = searchParams.camera;
      if (searchParams.dateFrom) params.dateFrom = searchParams.dateFrom;
      if (searchParams.dateTo) params.dateTo = searchParams.dateTo;

      const restOperation = get({
        apiName: 'PhotosynthesizeAPI',
        path: '/photos/search',
        options: {
          queryParams: params,
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        }
      });
      const { body } = await restOperation.response;
      const response = await body.json() as any;

      setResults(response?.photos || []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleReset = () => {
    setSearchParams({
      lens: '',
      camera: '',
      dateFrom: '',
      dateTo: ''
    });
    setResults([]);
    setSearched(false);
  };

  const getThumbnailUrl = (photo: Photo) => {
    const bucket = process.env.REACT_APP_THUMBNAILS_BUCKET;
    return `https://${bucket}.s3.amazonaws.com/${photo.paths.thumbnail_medium}`;
  };

  const getFilteredCameras = () => {
    if (!searchParams.camera) return cameraOptions.slice(0, 5);
    return cameraOptions
      .filter(camera => camera.toLowerCase().includes(searchParams.camera.toLowerCase()))
      .slice(0, 5);
  };

  const getFilteredLenses = () => {
    if (!searchParams.lens) return lensOptions.slice(0, 5);
    return lensOptions
      .filter(lens => lens.toLowerCase().includes(searchParams.lens.toLowerCase()))
      .slice(0, 5);
  };

  return (
    <div className="search-page">
      <h1>Search Photos</h1>
      <p className="search-subtitle">Search your photo library by camera, lens, or date</p>

      <form onSubmit={handleSearch} className="search-form">
        <div className="search-grid">
          <div className="search-field autocomplete-field">
            <label htmlFor="camera">Camera Model</label>
            <input
              id="camera"
              type="text"
              placeholder="e.g., Canon EOS R6 Mark III"
              value={searchParams.camera}
              onChange={e => setSearchParams({ ...searchParams, camera: e.target.value })}
              onFocus={() => setShowCameraSuggestions(true)}
              onBlur={() => setTimeout(() => setShowCameraSuggestions(false), 200)}
            />
            {showCameraSuggestions && getFilteredCameras().length > 0 && (
              <div className="autocomplete-dropdown">
                {getFilteredCameras().map(camera => (
                  <div
                    key={camera}
                    className="autocomplete-item"
                    onClick={() => {
                      setSearchParams({ ...searchParams, camera });
                      setShowCameraSuggestions(false);
                    }}
                  >
                    📷 {camera}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="search-field autocomplete-field">
            <label htmlFor="lens">Lens Model</label>
            <input
              id="lens"
              type="text"
              placeholder="e.g., RF 100-500mm F4.5-7.1 L IS USM"
              value={searchParams.lens}
              onChange={e => setSearchParams({ ...searchParams, lens: e.target.value })}
              onFocus={() => setShowLensSuggestions(true)}
              onBlur={() => setTimeout(() => setShowLensSuggestions(false), 200)}
            />
            {showLensSuggestions && getFilteredLenses().length > 0 && (
              <div className="autocomplete-dropdown">
                {getFilteredLenses().map(lens => (
                  <div
                    key={lens}
                    className="autocomplete-item"
                    onClick={() => {
                      setSearchParams({ ...searchParams, lens });
                      setShowLensSuggestions(false);
                    }}
                  >
                    🔭 {lens}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="search-field">
            <label htmlFor="dateFrom">From Date</label>
            <input
              id="dateFrom"
              type="date"
              value={searchParams.dateFrom}
              onChange={e => setSearchParams({ ...searchParams, dateFrom: e.target.value })}
            />
          </div>

          <div className="search-field">
            <label htmlFor="dateTo">To Date</label>
            <input
              id="dateTo"
              type="date"
              value={searchParams.dateTo}
              onChange={e => setSearchParams({ ...searchParams, dateTo: e.target.value })}
            />
          </div>
        </div>

        <div className="search-actions">
          <button type="submit" className="search-button" disabled={searching}>
            {searching ? 'Searching...' : 'Search'}
          </button>
          <button type="button" onClick={handleReset} className="reset-button">
            Reset
          </button>
        </div>
      </form>

      {searching && (
        <div className="search-loading">
          <div className="spinner"></div>
          <p>Searching your photos...</p>
        </div>
      )}

      {!searching && searched && (
        <div className="search-results">
          <h2>{results.length} Photo{results.length !== 1 ? 's' : ''} Found</h2>

          {results.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <p>No photos match your search criteria</p>
              <p className="no-results-hint">Try adjusting your search filters</p>
            </div>
          ) : (
            <div className="results-grid">
              {results.map(photo => (
                <div key={photo.photo_id} className="result-card">
                  <img
                    src={getThumbnailUrl(photo)}
                    alt={photo.filename}
                    className="result-thumbnail"
                  />
                  <div className="result-info">
                    <div className="result-filename">{photo.filename}</div>
                    <div className="result-meta">
                      {photo.exif?.camera_model && (
                        <div className="result-camera">
                          📷 {photo.exif.camera_model}
                        </div>
                      )}
                      {photo.exif?.lens_model && (
                        <div className="result-lens">
                          🔭 {photo.exif.lens_model}
                        </div>
                      )}
                      <div className="result-date">
                        📅 {new Date(photo.capture_date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Search;
